import type {Attribute} from '@/api/hoteldetails/types';
import type {HotelCategoryType} from '@travel-frontend/ui-components';

import {useBreakpointUp} from '@travel-frontend/core';
import {Stack} from '@travel-frontend/ui-components';
import {isValidElement} from 'react';

import {Bottom} from '@/components/HotelBoxInfo/components/Bottom';
import {Container} from '@/components/HotelBoxInfo/components/Container';
import {Controls} from '@/components/HotelBoxInfo/components/Controls';
import {InformationBadges} from '@/components/HotelBoxInfo/components/InformationBadges';
import {PriceRow} from '@/components/HotelBoxInfo/components/PriceRow';
import {Subtitle} from '@/components/HotelBoxInfo/components/Subtitle';
import {Top} from '@/components/HotelBoxInfo/components/Top';
import {HotelBoxTitle} from '@/components/HotelBoxTitle';
import {HotelCategory} from '@/components/HotelCategory';
import {HotelTypeBadge} from '@/components/HotelTypeBadge';
import {testId} from '@/utils/testId';

type Props = {
  category: number | React.ReactNode;
  categoryType: HotelCategoryType;
  title: React.ReactNode;
  subtitle: string | React.ReactNode;
  favoriteElement: React.ReactNode | null;
  promotionBadges?: React.ReactNode;
  hotelTypes?: Array<Attribute>;
  price: React.ReactNode;
  infoTrip: React.ReactNode;
  cta: React.ReactNode;
  message?: React.ReactNode;
  detailsLink?: null | React.ReactNode;
};

export const HotelBoxInfo: React.FunctionComponent<Props> = ({
  category,
  categoryType,
  title,
  subtitle,
  favoriteElement,
  promotionBadges,
  hotelTypes,
  price,
  infoTrip,
  cta,
  message,
  detailsLink,
}) => {
  const mdUp = useBreakpointUp('md');

  return (
    <Container data-testid="hotel-info">
      <Top spacing={0.75}>
        <Stack direction="row" justifyContent="space-between">
          <div>
            {isValidElement(category) ? (
              category
            ) : (
              <HotelCategory
                // @ts-expect-error ...
                value={category}
                type={categoryType}
                data-interaction-trigger
                data-testid={testId('hotel-category')}
              />
            )}
            <HotelBoxTitle data-testid={testId('hotel-name')}>
              {title}
            </HotelBoxTitle>
            <Subtitle data-testid={testId('hotel-region')}>{subtitle}</Subtitle>
          </div>
          {favoriteElement}
        </Stack>
        <InformationBadges>
          {hotelTypes && hotelTypes.length > 0 && (
            <HotelTypeBadge
              type={hotelTypes[0].name}
              name={hotelTypes[0].translation}
            />
          )}
          {promotionBadges}
        </InformationBadges>
      </Top>
      <Bottom>
        <PriceRow>
          <div>{infoTrip}</div>
          {price !== null && <div>{price}</div>}
        </PriceRow>
        {mdUp ? (
          <>
            {message}
            <Controls>
              {detailsLink || <>&nbsp;</>}
              {cta}
            </Controls>
          </>
        ) : (
          <>
            {detailsLink}
            {message}
            {cta}
          </>
        )}
      </Bottom>
    </Container>
  );
};
